<template>
  <div class="page">
    <div class="content" v-if="hasGeoData">
      <div class="menu">
        <h3>{{ "product_map.looking_for" | translate }}</h3>
        <div class="filter-toggle" @click="setIsFilterOpen(true)">
          <BIconSearch class="filter-icon" />
        </div>
      </div>

      <div class="close-btn" @click="onMapCloseClick">
        <BIconX class="close-icon-map" />
      </div>

      <div class="change-view" @click="onChangeViewClick">
        <p>
          {{ "product_map.change_view" | translate }}
        </p>
      </div>

      <!-- desktop filter -->
      <dropdown-desktop
        class="filter-toggle--desktop"
        :is-dropdown="isFilterOpen"
      >
        <div class="dropdown-content">
          <div class="title-row">
            <h3>{{ "product_map.filter" | translate }}</h3>
            <div class="icon-container" @click="setIsFilterOpen(false)">
              <BIconX class="close-icon" />
            </div>
          </div>
          <search-bar
            is-mobile
            class="search-bar"
            v-model="searchString"
            :placeholder="'header.search' | translate"
          />
          <div class="sex-selector-container"></div>
          <sex-selector
            class="sex-selector"
            :is-navigation="false"
            :value="sex"
            @input="setSex($event)"
          />
          <div class="filters">
            <div
              v-for="(filter, i) in selectsIncludingType"
              :key="i"
              class="filter-item"
            >
              <gyra-filter :filter="filter" @input="onFilterChanged()" />
            </div>
          </div>

          <h3 class="focus-title">{{ "product_map.focus" | translate }}</h3>
          <div class="toggle-row">
            <toggle
              class="toggle"
              emoji="sustainable"
              label="product_map.sustainable"
              @input="onToggleChange"
              v-model="sustainable"
            ></toggle>
            <toggle
              class="toggle"
              emoji="onSale"
              label="product_map.onSale"
              @input="onToggleChange"
              v-model="onSale"
            ></toggle>
          </div>

          <div class="apply-button" @click="applyFiltering()">
            <p>{{ "filter.messages.apply" | translate }}</p>
          </div>
        </div>
      </dropdown-desktop>

      <!-- mobile filter -->
      <slideup-mobile class="filter-toggle--mobile" :is-open="isFilterOpen">
        <filter-mobile
          include-type-select
          @close="setIsFilterOpen(false)"
          @filterChanged="onFilterChanged"
          @apply="applyFiltering()"
        />
      </slideup-mobile>

      <!-- Map -->
      <MglMap
        class="map"
        :accessToken="ACCESS_TOKEN"
        :mapStyle="MAP_STYLE"
        :interactive="true"
        :center="HELSINKI_CENTER"
        :zoom="12"
        :max-zoom="16"
        :min-zoom="9"
        :maxBounds="[
          [24.669913, 60.011244],
          [25.208299, 60.310934]
        ]"
        @load="onMapLoaded"
        @data="updateMarkers"
      >
        <MglGeojsonLayer
          sourceId="product_data_source"
          :source="getGeoData"
          layerId="product_clusters"
          :layer="clusterLayer"
          @click="onClusterClick($event)"
        />
        <MglGeojsonLayer
          sourceId="product_data_source"
          :source="getGeoData"
          layerId="product_clusters_count"
          :layer="countLayer"
        />
        <!-- <MglGeojsonLayer
          sourceId="product_data_source"
          :source="getGeoData"
          layerId="product_points"
          :layer="pointLayer"
          @click="onPointClicked"
        />-->
      </MglMap>
    </div>
    <gyra-spinner v-else />
    <product-map-modal :products="clusterProducts" />
  </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import Mapbox, { Marker } from "mapbox-gl";
import { MglGeojsonLayer, MglMap } from "vue-mapbox";
import {
  ACCESS_TOKEN,
  HELSINKI_CENTER_MOBILE_COORDINATES,
  MAP_STYLE,
  PRODUCT_MAP_CLUSTER_LAYER,
  PRODUCT_MAP_POINT_LAYER
} from "../constants";
import { parseProductsToGeoJSON } from "../utils";
import GyraSpinner from "../components/atoms/gyra-spinner";
import { PRODUCT_MAP_COUNT_LAYER } from "../constants/map/map";
import { BIconSearch, BIconX } from "bootstrap-vue";
import SearchBar from "../components/atoms/search-bar";
import SexSelector from "../components/atoms/sex-selector";
import { errorHandling } from "../mixins";
import ProductMapModal from "../components/organisms/product-map-modal";
import GyraFilter from "../components/organisms/gyra-filter";
import { filterMixin } from "../mixins/filters/filterMixin";
import { mapGetters, mapMutations } from "vuex";
import Toggle from "../components/atoms/toggle";
import DropdownDesktop from "../components/molecules/dropdown-desktop";
import { filterModelMixin } from "../mixins/filters/filterModelMixin";
import SlideupMobile from "../components/molecules/slideup-mobile";
import FilterMobile from "../components/organisms/filter-mobile";
import { productTypeNavigation } from "../mixins/navigation/productTypeNavigation";

export default {
  name: "ProductMap",
  components: {
    FilterMobile,
    SlideupMobile,
    DropdownDesktop,
    Toggle,
    GyraFilter,
    ProductMapModal,
    SexSelector,
    SearchBar,
    GyraSpinner,
    MglMap,
    MglGeojsonLayer,
    BIconSearch,
    BIconX
  },
  mixins: [errorHandling, filterMixin, filterModelMixin, productTypeNavigation],
  data() {
    return {
      ACCESS_TOKEN: ACCESS_TOKEN,
      MAP_STYLE: MAP_STYLE,
      HELSINKI_CENTER: HELSINKI_CENTER_MOBILE_COORDINATES,
      clusterLayer: PRODUCT_MAP_CLUSTER_LAYER,
      countLayer: PRODUCT_MAP_COUNT_LAYER,
      pointLayer: PRODUCT_MAP_POINT_LAYER,

      clusterProducts: [],

      isFilterOpen: false
    };
  },
  methods: {
    async getProducts() {
      const products = await this.applyFilters(100000);
      if (products) {
        this.setProducts(products);
      }
      if (products.length === 0) {
        this.handleWarning("errors.warning.nothing_found");
      }
    },
    applyFiltering() {
      this.getProducts();
      this.setIsFilterOpen(false);

      const productType = this.selectsIncludingType.find(
        select => select.key === "type"
      ).model;
      if (productType) {
        this.setSubNavItemWithFilter(productType);
      }
    },

    async onMapLoaded(event) {
      this.map = event.map;
      // event.map.on("styleimagemissing", function(e) {
      //   const id = e.id;
      //
      //   event.map.loadImage(id, function(err, img) {
      //     if (err) this.handleGraveError(err);
      //     if (!event.map.hasImage(id)) {
      //       event.map.addImage(id, img);
      //     }
      //   });
      // });
    },
    onToggleChange() {
      this.setSelectOptions();
    },
    updateMarkers(event) {
      let newMarkers = {};
      const features = event.map.querySourceFeatures("product_data_source");

      features.forEach(feature => {
        const coords = feature.geometry.coordinates;
        const props = feature.properties;
        if (props.cluster) return;
        const id = props._id;

        let marker = this.markers[id];
        if (!marker) {
          const imgs = props.imageSources.split(",");
          const img = imgs[0];
          const cleanedImg = img.replace('["', "").replace('"', "");
          const html = `<div class="product-marker" style="background-image: url('${cleanedImg}')" id="marker-${props._id}" "></div>`;
          const el = document.createElement("div");
          el.innerHTML = html;
          el.firstChild.addEventListener("click", this.onMarkerClick);
          marker = this.markers[id] = new Marker({
            element: el.firstChild
          }).setLngLat(coords);
        }
        newMarkers[id] = marker;

        if (!this.markersOnScreen[id]) marker.addTo(event.map);
      });

      for (let id in this.markersOnScreen) {
        if (!newMarkers[id]) this.markersOnScreen[id].remove();
      }
      this.markersOnScreen = newMarkers;
    },
    async onFilterChanged() {
      await this.setSelectOptions();
    },
    setIsFilterOpen(bool) {
      this.isFilterOpen = bool;
    },
    onMarkerClick(event) {
      const id = event.target.id.replace("marker-", "");
      this.$router.push({
        name: "ProductPage",
        params: {
          id: id
        }
      });
    },
    setSex($event) {
      this.resetCommons();
      this.sex = $event === "general.women" ? "women" : "men";
      this.setSelectOptions();
    },
    onPointClicked(event) {
      const { map, mapboxEvent } = event;
      const features = map.queryRenderedFeatures(mapboxEvent.point);
      const product = features[0].properties;

      this.visitProductPage(product);
    },
    onClusterClick(payload) {
      const { map, mapboxEvent } = payload;

      const features = map.queryRenderedFeatures(mapboxEvent.point);
      const clusterId = features[0].properties.cluster_id;
      const point_count = features[0].properties.point_count;
      const clusterSource = map.getSource("product_data_source");

      clusterSource.getClusterExpansionZoom(clusterId, (_, zoom) => {
        if (zoom === 18) {
          clusterSource.getClusterLeaves(
            clusterId,
            point_count,
            0,
            (_, features) => {
              this.clusterProducts = features.map(
                feature => feature.properties
              );
              this.$bvModal.show("product_map_modal");
            }
          );
        } else {
          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom
          });
        }
      });
    },
    visitProductPage(product) {
      this.$router.push({
        name: "ProductPage",
        params: {
          name: product.name,
          id: product._id,
          sex: this.$route.params.sex
        }
      });
    },
    onMapCloseClick() {
      this.$router.go(-1);
    },
    onChangeViewClick() {
      const category = this.category || "clothing";
      this.$router.push({ name: category });
    },
    ...mapMutations({
      setFilterFieldByKeyValue: "filters/setFieldByKeyValue",
      setOneSelectValue: "filters/setOneSelectValue",
      setProducts: "overview/setProducts",
      resetCommons: "filters/resetCommons"
    })
  },
  computed: {
    getGeoData() {
      if (this.products === null) return null;
      return parseProductsToGeoJSON(this.products);
    },
    hasGeoData() {
      return this.products;
    },
    ...mapGetters({
      selectsIncludingType: "filters/getSelectsWithTypeSelect",
      products: "overview/getProducts",
      isAFilterApplied: "filters/isANonNavFilterApplied"
    })
  },
  created() {
    this.map = null;
    this.markers = {};
    this.markersOnScreen = {};
  },
  mounted() {
    if (!this.products) {
      this.getProducts();
    }
    this.setSelectOptions();
  },
  beforeDestroy() {
    this.setOneSelectValue({ key: "type", value: null });
    if (this.map) {
      if (this.map.getLayer("product_clusters")) {
        this.map.removeLayer("product_clusters");
      }
      if (this.map.getLayer("product_clusters_count")) {
        this.map.removeLayer("product_clusters_count");
      }
      if (this.map.getLayer("pointLayer")) {
        this.map.removeLayer("pointLayer");
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "~@/styles/global.scss";
.page {
  height: 100vh;
  width: 100%;
  position: relative;
  z-index: 30;
}
.content {
  height: 100vh;
  width: 100%;
}
.menu {
  position: absolute;
  width: 100%;
  top: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h3 {
    width: 90%;
    text-align: center;

    font-size: 20px;

    @media (min-width: $gyra-breakpoint-sm) {
      font-size: 30px;
    }
  }

  * {
    z-index: 10;
  }
}

.filter-toggle {
  padding: 5px;
  margin-top: 28px;
  @media (min-width: $gyra-breakpoint-sm) {
    width: 52px;
    height: 52px;
  }
  width: 34px;
  height: 34px;
  background-color: $gyra-grey-dark;
  border-radius: 12px;
  box-shadow: 0 12px 16px 0 rgba(69, 91, 99, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}
.filter-toggle--desktop {
  display: none;
  @media (min-width: $gyra-breakpoint-sm) {
    display: flex;
  }
}
.filter-toggle--mobile {
  display: flex;
  @media (min-width: $gyra-breakpoint-sm) {
    display: none;
  }
}

.filter-icon {
  color: white;
  width: 80%;
  height: auto;
}

.title-row {
  margin-top: 90px;
  display: flex;
  flex-direction: row;

  .close-icon {
    width: 24px;
    height: 24px;
  }

  .icon-container {
    position: absolute;
    right: 0;
    margin-right: 116px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.search-bar {
  margin-top: 40px;
  height: 51.4px;
  width: 442px;
}

.sex-selector-container {
  margin-top: 40px;
  width: 180px;
  height: 34px;
  margin-right: auto;
}
.sex-selector {
  transform: scale(1.2);
  height: 34px;
  width: 180px;
}

.filters {
  margin-top: 40px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
  width: 90%;
}

.filter-item {
  margin-top: 30px;
  min-width: 150px;
}

.apply-button {
  margin-top: 75px;
  margin-bottom: 20px;
  width: 212px;
  height: 45px;
  border-radius: 10px;
  box-shadow: 10px 10px 30px 0 rgba(0, 0, 0, 0.16);
  background-color: $gyra-grey-dark;
  display: flex;
  justify-content: center;
  cursor: pointer;

  p {
    color: white;
  }
}

.focus-title {
  margin-top: 59px;
}

h3 {
  font-size: 30px;
}

.toggle-row {
  margin-top: 30px;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  flex-wrap: wrap;
  @media (min-width: $gyra-breakpoint-lg) {
    width: 40%;
  }
  width: 60%;
}

.toggle {
  transform: scale(1.2);
}

.dropdown-content {
  width: 100%;
  min-height: 750px;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}

.product-marker {
  width: 100px;
  height: 100px;
  color: red;
}

.close-btn {
  position: absolute;
  top: 35px;
  right: 25px;
  @media (min-width: $gyra-breakpoint-sm) {
    top: 100px;
    right: 100px;
  }
  z-index: 10;
  width: 20px;
  height: 20px;
  background-color: $gyra-grey-dark;
  border-radius: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.close-icon-map {
  color: white;
  font-size: 20px;
  font-weight: bold;
}

.change-view {
  position: absolute;
  padding: 0 10px;
  bottom: 80px;
  right: 20px;
  z-index: 10;
  cursor: pointer;
  border-radius: 20px;
  background-color: $gyra-salmon;
  p {
    color: $gyra-grey-dark;
    margin: 0;
    font-size: 10px;
    font-weight: bold;
    text-decoration: underline;
  }
}
</style>

<style lang="scss">
@import "~@/styles/global.scss";
.product-marker {
  width: 50px;
  height: 50px;
  border-radius: 100px;
  background-size: cover;
  background-position: center;
}
</style>
